// AI Assistant — three focused tools backed by /api/v1/ai/*: draft email,
// summarize text, suggest next action. Optionally scoped to a client (the
// agent picks one to feed context).

import { useMemo, useState } from 'react';
import { Sparkles, Mail, FileText, Lightbulb, Loader2, Copy, Check } from 'lucide-react';
import { aiDraftEmail, aiSummarize, aiSuggestNextAction } from '../api.js';
import { toast } from './Toast.jsx';
import { motivoDelFalloDeIA } from '../fallosDeIA.js';

const TABS = [
  { id: 'email',     en: 'Draft email',          es: 'Redactar email',     icon: Mail },
  { id: 'summarize', en: 'Summarize text',       es: 'Resumir texto',      icon: FileText },
  { id: 'suggest',   en: 'Suggest next action',  es: 'Sugerir próximo paso', icon: Lightbulb },
];

function buildClientSnapshot(client, policies) {
  if (!client) return '';
  const lines = [];
  lines.push(`Name: ${client.firstName || ''} ${client.lastName || ''}`.trim());
  if (client.email) lines.push(`Email: ${client.email}`);
  if (client.phone) lines.push(`Phone: ${client.phone}`);
  if (client.status) lines.push(`Status: ${client.status}`);
  if (client.dob) lines.push(`DOB: ${client.dob}`);
  if (client.preferredLang) lines.push(`Preferred language: ${client.preferredLang}`);
  if (client.notes) lines.push(`Notes: ${String(client.notes).slice(0, 600)}`);
  const cp = (policies || []).filter(p => p.clientId === client.id && p.status !== 'cancelled' && p.status !== 'inactive');
  if (cp.length > 0) {
    lines.push(`Active policies (${cp.length}):`);
    for (const p of cp.slice(0, 5)) {
      const exp = p.expirationDate || p.renewalDate || '';
      lines.push(`  - ${p.type || 'policy'} · ${p.carrier || ''} · $${p.monthlyPremium || 0}/mo · exp ${exp}`);
    }
  }
  return lines.join('\n');
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button onClick={onCopy} className="dkt-btn-g" style={{ padding: '.35rem .65rem', fontSize: '.7rem' }}>
      {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
    </button>
  );
}

export function AIAssistantView({ clients = [], policies = [], lang = 'en' }) {
  const es = lang === 'es';
  const [tab, setTab] = useState('email');
  const [clientId, setClientId] = useState('');
  const client = useMemo(() => clients.find(c => c.id === clientId) || null, [clients, clientId]);
  const snapshot = useMemo(() => buildClientSnapshot(client, policies), [client, policies]);

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div className="dkt-sec-title">
          <Sparkles size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
          {es ? 'Asistente AI' : 'AI Assistant'}
        </div>
        <div className="dkt-sec-sub">
          {es ? 'Powered by Claude. Redacta emails, resume texto y sugiere próximos pasos.' : 'Powered by Claude. Draft emails, summarize text, suggest next steps.'}
        </div>
      </div>

      <div className="dkt-card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <label className="dkt-input-lbl">{es ? 'Cliente (opcional, da contexto al AI)' : 'Client (optional, gives AI context)'}</label>
        <select className="dkt-inp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">{es ? '-- sin cliente específico --' : '-- no specific client --'}</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: '1rem', flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`dkt-tab${tab === t.id ? ' on' : ''}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon size={12} /> {es ? t.es : t.en}
            </button>
          );
        })}
      </div>

      {tab === 'email' && <DraftEmailPanel snapshot={snapshot} lang={lang} />}
      {tab === 'summarize' && <SummarizePanel lang={lang} />}
      {tab === 'suggest' && <SuggestPanel snapshot={snapshot} lang={lang} />}
    </div>
  );
}

function DraftEmailPanel({ snapshot, lang }) {
  const es = lang === 'es';
  const [purpose, setPurpose] = useState('');
  const [tone, setTone] = useState('friendly');
  const [outLang, setOutLang] = useState(lang === 'es' ? 'es' : 'en'); // language of the GENERATED email
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!purpose.trim()) { toast.warn(es ? 'Describe el propósito del email' : 'Describe the email purpose'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await aiDraftEmail({ purpose, tone, lang: outLang, clientContext: snapshot });
      setResult(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dkt-card" style={{ padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
      <div>
        <label className="dkt-input-lbl">{es ? 'Propósito' : 'Purpose'}</label>
        <textarea className="dkt-inp" rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)}
          placeholder={es ? 'Ej: Recordarle que su póliza vence en 30 días y ofrecer cita para renovar' : 'e.g. Remind them their policy expires in 30 days and offer a renewal call'} />
      </div>
      <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="dkt-input-lbl" style={{ marginBottom: 0 }}>{es ? 'Tono:' : 'Tone:'}</label>
        {['friendly', 'formal', 'urgent'].map(t => (
          <button key={t} onClick={() => setTone(t)} className={`dkt-tab${tone === t ? ' on' : ''}`}>
            {es ? ({ friendly: 'Amistoso', formal: 'Formal', urgent: 'Urgente' }[t]) : t}
          </button>
        ))}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--c-border,#1a3550)', margin: '0 2px' }} />
        <label className="dkt-input-lbl" style={{ marginBottom: 0 }}>{es ? 'Idioma:' : 'Language:'}</label>
        <button onClick={() => setOutLang('es')} className={`dkt-tab${outLang === 'es' ? ' on' : ''}`}>🇪🇸 ES</button>
        <button onClick={() => setOutLang('en')} className={`dkt-tab${outLang === 'en' ? ' on' : ''}`}>🇺🇸 EN</button>
        <div style={{ flex: 1 }} />
        <button className="dkt-btn-p" onClick={run} disabled={busy}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <><Sparkles size={13} /> {es ? 'Generar' : 'Generate'}</>}
        </button>
      </div>

      {result && (
        <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{es ? 'Asunto' : 'Subject'}</div>
            <CopyButton text={`Subject: ${result.subject}\n\n${result.body}`} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: 12 }}>{result.subject}</div>
          <div style={{ fontSize: 13, color: 'var(--c-text,#c8dff0)', lineHeight: 1.6, whiteSpace: 'pre-wrap', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: 10 }}>
            {result.body}
          </div>
        </div>
      )}
    </div>
  );
}

function SummarizePanel({ lang }) {
  const es = lang === 'es';
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (text.trim().length < 30) { toast.warn(es ? 'Pega al menos un par de oraciones' : 'Paste at least a sentence or two'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await aiSummarize({ text, lang });
      setResult(r);
    } catch (e) {
      // El texto se manda ENTERO: recortarlo a 16.000 en el servidor devolvía
      // el resumen de los primeros dos tercios con cara de resumen completo, y
      // en una transcripción de llamada el desenlace está al final (E-283).
      // Ahora, si no cabe, lo dice — y aquí se traduce a algo accionable.
      toast.error((es ? 'No se pudo resumir: ' : 'Could not summarize: ') + motivoDelFalloDeIA(e, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dkt-card" style={{ padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
      <div>
        <label className="dkt-input-lbl">{es ? 'Pega el texto a resumir' : 'Paste text to summarize'}</label>
        <textarea className="dkt-inp" rows={6} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={es ? 'Notas de la llamada, conversación de email, transcripción...' : 'Call notes, email thread, transcript...'} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="dkt-btn-p" onClick={run} disabled={busy}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <><Sparkles size={13} /> {es ? 'Resumir' : 'Summarize'}</>}
        </button>
      </div>

      {result && (
        <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '1rem' }}>
          <div style={{ fontSize: 11, color: 'var(--c-cyan,#00d4ff)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>TL;DR</div>
          <div style={{ fontSize: 13, color: 'var(--c-white,#eef6ff)', marginBottom: 12, lineHeight: 1.5 }}>{result.tldr}</div>

          {result.keyFacts?.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--c-cyan,#00d4ff)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>{es ? 'Hechos clave' : 'Key facts'}</div>
              <ul style={{ margin: 0, paddingLeft: 18, marginBottom: 12, fontSize: 13, color: 'var(--c-text,#c8dff0)', lineHeight: 1.6 }}>
                {result.keyFacts.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </>
          )}

          {result.nextActions?.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#00ff88', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>{es ? 'Próximos pasos' : 'Next actions'}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--c-text,#c8dff0)', lineHeight: 1.6 }}>
                {result.nextActions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestPanel({ snapshot, lang }) {
  const es = lang === 'es';
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!snapshot.trim()) { toast.warn(es ? 'Selecciona un cliente arriba' : 'Pick a client above'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await aiSuggestNextAction({ clientContext: snapshot, lang });
      setResult(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dkt-card" style={{ padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
      {snapshot ? (
        <pre style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.75rem', fontSize: 11, color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', margin: 0, maxHeight: 180, overflow: 'auto' }}>
          {snapshot}
        </pre>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1rem' }}>
          {es ? 'Selecciona un cliente arriba para ver sugerencias.' : 'Pick a client above to see suggestions.'}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="dkt-btn-p" onClick={run} disabled={busy || !snapshot}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <><Sparkles size={13} /> {es ? 'Sugerir' : 'Suggest'}</>}
        </button>
      </div>

      {result?.suggestions?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          {result.suggestions.map((s, i) => (
            <div key={i} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderLeft: '3px solid var(--c-cyan,#00d4ff)', borderRadius: 9, padding: '.75rem 1rem' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--c-white,#eef6ff)', marginBottom: 4 }}>{i + 1}. {s.title}</div>
              <div style={{ fontSize: 12, color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>{s.reason}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
